// frontend/src/services/driverService.js
// Responsibility: Centralize driver-related API calls for the app
// (listing, creating, photo upload/delete, banned drivers, etc.)

import axios from 'axios';

/**
 * Fetches drivers for the current agency, with optional filters.
 * @param {Object}   [opts]
 * @param {'Active'|'Reserved'|'Banned'} [opts.status] – status to filter by
 * @param {number}   [opts.agencyId]                   – agency to filter by
 * @param {string}   [opts.search]                     – partial name match
 * @returns {Promise<Driver[]>}
 */
export async function fetchDrivers({ status, agencyId, search } = {}) {
  const params = {};
  if (status)   params.status    = status;
  if (agencyId) params.agency_id = agencyId; // match backend expectation
  if (search)   params.search    = search;

  const { data } = await axios.get('/agencies/drivers', { params });

  // Normalize to an array no matter what the backend shape is
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.drivers)) return data.drivers;

  console.warn('[driverService.fetchDrivers] unexpected response:', data);
  return [];
}

/**
 * Adds a new driver under the current agency.
 * @param {{ name: string, vehicle_registration_number: string, phone?: string, email?: string }} driverData
 * @returns {Promise<Driver>}
 */
export async function addDriver(driverData) {
  const { data } = await axios.post('/agencies/drivers', driverData);
  return data;
}

/**
 * Fetches the UPS-specific banned list, with optional filters.
 * @param {Object} [opts]
 * @param {number} [opts.agencyId]
 * @param {string} [opts.search]
 * @returns {Promise<DriverBan[]>}
 */
export async function fetchBannedDrivers({ agencyId, search } = {}) {
  const params = {};
  if (agencyId) params.agency_id = agencyId;
  if (search)   params.search    = search;

  const { data } = await axios.get('/ups/drivers/banned', { params });
  return data;
}

/**
 * Upload or replace a driver's photo.
 * Backend endpoint:
 *   POST /ups/drivers/:id/photo
 *
 * Expected response:
 *   { message: 'Photo uploaded', driver, version }
 *
 * We return ONLY data.driver so Redux thunks/components get the updated
 * driver object directly and can do upsertOne/upsertDriver cleanly.
 */
export async function uploadDriverPhoto(id, file) {
  const formData = new FormData();
  formData.append('photo', file);

  const { data } = await axios.post(`/ups/drivers/${id}/photo`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  // If your backend ALWAYS returns { message, driver, version },
  // you can safely just:
  //   return data.driver;
  //
  // Keeping a small safeguard in case shape changes:
  if (!data || !data.driver) {
    console.warn('[driverService.uploadDriverPhoto] unexpected response:', data);
    return data?.driver ?? data;
  }

  return data.driver;
}

/**
 * Delete a driver's photo.
 * Backend endpoint:
 *   DELETE /ups/drivers/:id/photo
 *
 * Expected response:
 *   { message: 'Photo removed', driver, version }
 */
export async function deleteDriverPhoto(id) {
  const { data } = await axios.delete(`/ups/drivers/${id}/photo`);

  if (!data || !data.driver) {
    console.warn('[driverService.deleteDriverPhoto] unexpected response:', data);
    return data?.driver ?? data;
  }

  return data.driver;
}
